import { useRef } from 'react';

import { Box } from '@opendoor/bricks-next';
import { SimilarListings } from 'components/marketplace/property';
import type { PropertyDetailsType } from 'components/marketplace/property';
import { Divider } from 'components/shared/Divider';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import dynamic from 'next/dynamic';

import { PropertyCTAs } from './PropertyCTAs';
import { PropertyDetails } from './PropertyDetails';
import { PropertyStatus } from './PropertyStatus';
import { PropertySummary } from './PropertySummary';
import { RentalScamBanner } from './RentalScamBanner';

const FooterCTA = dynamic(
  () => import('components/marketplace/property/FooterCTA').then((m) => m.FooterCTA),
  {
    ssr: false,
  },
);

type PropertySectionProps = {
  property: PropertyDetailsType;
};

export const PropertySection = ({ property }: PropertySectionProps) => {
  const ctaRef = useRef<HTMLDivElement>(null);
  const { inView } = useInViewAfterLoad({ ref: ctaRef, threshold: 0, initialInView: true });

  return (
    <Box
      flexDirection="column"
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      $largerThanSM={{ flexBasis: '50%', overflowY: 'scroll' }}
      $largerThanLG={{ maxWidth: '$736x' }}
    >
      <Box $largerThanSM={{ px: 48 }} $smallerThanMD={{ px: 24 }} pt={24}>
        <RentalScamBanner />
        <PropertyStatus property={property} />
        <PropertySummary property={property} />
        <Box ref={ctaRef}>
          <PropertyCTAs property={property} />
        </Box>
        <Divider mt="$24x" />
        <PropertyDetails property={property} />
        <Box tag="section">
          <SimilarListings listings={property?.similarProperties?.results || []} />
        </Box>
      </Box>
      <FooterCTA property={property} isVisible={!inView} />
    </Box>
  );
};
